import { gsap } from "gsap";

import { IReelData } from "../../../abstracts/elements/AbstractReel";
import { AbstractGameMode } from "../../../abstracts/flow/AbstractGameMode";
import { TimelineLabels } from "../../../abstracts/types/enums";
import { IScatterDecoratorData } from "../../../abstracts/types/interfaces";
import { isAllTrue, shuffleArray } from "../../../abstracts/utils/ArrayUtils";
import { playSound, stopSound } from "../../../abstracts/utils/SoundUtils";
import { davidPanningReelStop } from "../../UCConfig";
import { UgliestCatch } from "../../UgliestCatch";
import { UCSoundNames } from "../../assetsLoader/UCAssetsNames";
import UCReel from "../../modules/Reels/UCReel";
import { playBlockerOpenSound } from "../../modules/Reels/UCReelBlocker";
import { UCScatterSymbol } from "../../modules/Reels/symbols/UCScatterSymbol";
import { BlockerStates, ScatterStates, UCSymbolNames } from "../../types/UCEnums";

const scatterLandingSounds: UCSoundNames[] = [
    UCSoundNames.SCATTER_LANDING_1,
    UCSoundNames.SCATTER_LANDING_2,
    UCSoundNames.SCATTER_LANDING_3,
    UCSoundNames.SCATTER_LANDING_4,
    UCSoundNames.SCATTER_LANDING_5,
];

const scatterAnticipationLandingSounds: UCSoundNames[] = [
    UCSoundNames.SCATTER_ANTICIPATION_LAND_1,
    UCSoundNames.SCATTER_ANTICIPATION_LAND_2,
    UCSoundNames.SCATTER_ANTICIPATION_LAND_3,
];

export const UCResultDecorator = (data: IScatterDecoratorData): void => {
    const {
        scatterSymbolName = `scatter`,
        finalDataReels = [],
        gameReels = [],
        restrictedReelsByIndex = [],
        timeline = gsap.timeline(),
        stopSpinDelay = 0,
        nearWinDuration = 0.5,
        wasFeatureBuy = false,
        minScattersToTriggerFeature = 3,
        onDecoratorComplete = () => {},
        gameMode = AbstractGameMode.NORMAL,
    } = data;

    const minScattersToTriggerNearWin: number = minScattersToTriggerFeature - 1;

    let scattersCounter: number = 0;

    let nearWinOnReels: boolean[] = [];

    finalDataReels.forEach((symbols: string[], reelIndex: number) => {
        let hasNearWin: boolean = false;

        // if (scattersCounter === minScattersToTriggerNearWin) {
        if (scattersCounter >= minScattersToTriggerNearWin) {
            if (!restrictedReelsByIndex.includes(reelIndex)) {
                hasNearWin = true;
            }
        }

        let symbolsIncludeScatter: boolean = false;
        symbols.forEach((symbolName: string) => {
            if (symbolName === scatterSymbolName) {
                symbolsIncludeScatter = true;
            }
        });

        if (symbolsIncludeScatter) {
            scattersCounter++;
        }

        nearWinOnReels[reelIndex] =
            hasNearWin &&
            gameMode === AbstractGameMode.NORMAL &&
            (!wasFeatureBuy || UgliestCatch.model.isLuckyDraw || UgliestCatch.model.isXbet);
    });

    timeline.addLabel(TimelineLabels.SCATTER_DECORATOR_START, `>`);

    const revealFSSymbolsTimeline: gsap.core.Timeline = gsap.timeline({});

    if (gameMode !== AbstractGameMode.NORMAL) {
        if (UgliestCatch.model.data.extraPositionsOpen.includes(true)) {
            const revealTopFSBlockersTimeline: gsap.core.Timeline = gsap.timeline();
            const revealBottomFSBlockersTimeline: gsap.core.Timeline = gsap.timeline();
            let counter = 0;

            UgliestCatch.model.data.extraPositionsOpen.forEach((isOpen, reelIndex) => {
                if (
                    isOpen &&
                    (gameReels[reelIndex] as UCReel).topBlocker.state === BlockerStates.OPEN
                ) {
                    revealTopFSBlockersTimeline.add(
                        (gameReels[reelIndex] as UCReel).topBlocker.revealFsSymbol(
                            finalDataReels[reelIndex][0] as UCSymbolNames,
                            gameMode === AbstractGameMode.SUPER_FREESPIN
                        ),
                        counter * 0.1
                    );

                    revealBottomFSBlockersTimeline.add(
                        (gameReels[reelIndex] as UCReel).bottomBlocker.revealFsSymbol(
                            finalDataReels[reelIndex][4] as UCSymbolNames,
                            gameMode === AbstractGameMode.SUPER_FREESPIN
                        ),
                        counter * 0.1
                    );
                    counter++;
                }
            });

            revealFSSymbolsTimeline.add(revealTopFSBlockersTimeline);
            revealFSSymbolsTimeline.add(revealBottomFSBlockersTimeline, `>-=0.1`);
        }
    }

    timeline.add(revealFSSymbolsTimeline, TimelineLabels.SCATTER_DECORATOR_START);
    const initialRevealFinishedLabel: string = `initialRevealFinished`;
    timeline.addLabel(initialRevealFinishedLabel, `>`);

    let currentTimelinePosition: number = timeline.labels[initialRevealFinishedLabel];
    // let scatterSoundsPlayCounter: number = 0;
    const stoppedReels: boolean[] = new Array(finalDataReels.length).fill(false);

    const onReelSpinComplete = (reelId: number): void => {
        stoppedReels[reelId] = true;

        if (isAllTrue(stoppedReels)) {
            let counter = 0;
            const revealUnlockedSymbolsTimeline: gsap.core.Timeline = gsap.timeline({
                onComplete: () => {
                    onDecoratorComplete();
                },
            });
            const revealTopBlockersTimeline: gsap.core.Timeline = gsap.timeline();
            const revealBottomBlockersTimeline: gsap.core.Timeline = gsap.timeline();

            finalDataReels.forEach((reel, reelIndex) => {
                if (reel.includes(scatterSymbolName)) {
                    revealTopBlockersTimeline.add(
                        (gameReels[reelIndex] as UCReel).topBlocker.revealNormalSymbol(),
                        counter * 0.1
                    );
                    revealBottomBlockersTimeline.add(
                        (gameReels[reelIndex] as UCReel).bottomBlocker.revealNormalSymbol(),
                        counter * 0.1
                    );
                    counter++;
                }
            });

            if (counter > 0) {
                revealUnlockedSymbolsTimeline.add(revealTopBlockersTimeline);
                const topTlDuration: number = revealTopBlockersTimeline.totalDuration();

                revealUnlockedSymbolsTimeline.add(
                    revealBottomBlockersTimeline,
                    topTlDuration - 0.2
                );
            }
        }
    };

    let firstNearWinToStop: boolean = true;
    shuffleArray(scatterAnticipationLandingSounds);
    let currentAnticipationLandSound = 0;
    let reelStopTimeline: gsap.core.Timeline = gsap.timeline();

    let totalScattersStoppedOnLanding: number = 0;
    let winningScatters: UCScatterSymbol[] = [];
    let scatterState: ScatterStates = ScatterStates.NORMAL;
    let totalReelsLanded: number = 0;

    const onReelLand = (reelId: number): void => {
        totalReelsLanded++;

        if (nearWinOnReels[reelId]) {
            playSound({
                name: scatterAnticipationLandingSounds[currentAnticipationLandSound],
            });
            currentAnticipationLandSound++;
        }

        if (finalDataReels[reelId].toString().includes(scatterSymbolName)) {
            totalScattersStoppedOnLanding++;

            if (gameMode !== AbstractGameMode.NORMAL) {
                const openedPositionsLength: number =
                    UgliestCatch.model.data.extraPositionsOpen.filter(
                        (isOpen) => isOpen === true
                    ).length;

                totalScattersStoppedOnLanding +=
                    openedPositionsLength - totalScattersStoppedOnLanding;

                playSound({
                    name: scatterLandingSounds[totalScattersStoppedOnLanding - 1],
                    stereo: davidPanningReelStop[totalScattersStoppedOnLanding - 1],
                });
            } else {
                if (
                    totalScattersStoppedOnLanding === minScattersToTriggerNearWin &&
                    reelId === finalDataReels.length - 1
                ) {
                    if (UgliestCatch.model.isXbet) {
                        playSound({ name: UCSoundNames.SCATTER_LANDING_1_XBET });
                    } else {
                        playSound({ name: UCSoundNames.SCATTER_LANDING_2 });
                    }
                    totalScattersStoppedOnLanding -= 1;
                } else {
                    // if (reelId + 1 < 5) {
                    if (
                        totalScattersStoppedOnLanding === minScattersToTriggerNearWin &&
                        (!wasFeatureBuy ||
                            UgliestCatch.model.isLuckyDraw ||
                            UgliestCatch.model.isXbet)
                    ) {
                        playSound({
                            name: UCSoundNames.SCATTER_ANTICIPATION,
                        });
                    }

                    // if (gameMode === AbstractGameMode.NORMAL) {
                    if (reelId === 1) {
                        if (totalScattersStoppedOnLanding === 1) {
                            if (UgliestCatch.model.isXbet) {
                                playSound({
                                    name: UCSoundNames.SCATTER_LANDING_1_XBET,
                                    stereo: davidPanningReelStop[totalScattersStoppedOnLanding - 1],
                                });
                            } else {
                                playSound({
                                    name: UCSoundNames.SCATTER_LANDING_1,
                                    stereo: davidPanningReelStop[totalScattersStoppedOnLanding - 1],
                                });
                            }
                        } else {
                            playSound({
                                name: scatterLandingSounds[totalScattersStoppedOnLanding - 1],
                                stereo: davidPanningReelStop[totalScattersStoppedOnLanding - 1],
                            });
                        }
                    } else {
                        if (
                            wasFeatureBuy &&
                            !UgliestCatch.model.isLuckyDraw &&
                            totalScattersStoppedOnLanding > 2
                        ) {
                            stopSound({
                                name: scatterLandingSounds[totalScattersStoppedOnLanding - 2],
                            });
                        }

                        playSound({
                            name: scatterLandingSounds[totalScattersStoppedOnLanding - 1],
                            stereo: davidPanningReelStop[totalScattersStoppedOnLanding - 1],
                        });
                    }
                }
            }
        }

        if (
            reelId === finalDataReels.length - 1 &&
            totalScattersStoppedOnLanding <= minScattersToTriggerNearWin
        ) {
            if (totalScattersStoppedOnLanding === minScattersToTriggerNearWin) {
                playSound({ name: UCSoundNames.SCATTER_ANTICIPATION_FAIL });
            }
        }

        if (
            reelId === finalDataReels.length - 1 &&
            totalScattersStoppedOnLanding > minScattersToTriggerNearWin &&
            (!wasFeatureBuy || UgliestCatch.model.isLuckyDraw || UgliestCatch.model.isXbet)
        ) {
            playSound({ name: UCSoundNames.SCATTER_ANTICIPATION_WIN });
        }

        //////////////////handle scatters win animation
        if (finalDataReels[reelId].toString().includes(scatterSymbolName)) {
            winningScatters.push((gameReels[reelId] as UCReel).scatterSymbol);

            if (winningScatters.length === minScattersToTriggerNearWin) {
                scatterState = ScatterStates.ANTICIPATION;
            } else if (winningScatters.length >= 3) {
                scatterState = ScatterStates.WIN;
            }
        }

        if (totalReelsLanded === 5 && winningScatters.length <= minScattersToTriggerNearWin) {
            if (winningScatters.length === minScattersToTriggerNearWin) {
                scatterState = ScatterStates.LOSE;
            } else {
                scatterState = ScatterStates.NORMAL;
            }
        }

        if (wasFeatureBuy && !UgliestCatch.model.isLuckyDraw) {
            if (scatterState === ScatterStates.WIN) {
                if (totalReelsLanded === 5) {
                    winningScatters.forEach((scatter) => {
                        scatter.getWinAnimation(scatterState);
                    });
                }
            }
        } else {
            winningScatters.forEach((scatter) => {
                reelStopTimeline.add(scatter.getWinAnimation(scatterState), 0);
            });
        }

        reelStopTimeline.resume();
        //////////////////
    };

    gameReels.forEach((reel, reelIndex) => {
        (reel as UCReel).topBlocker.isOpenDataFromServer =
            UgliestCatch.model.data.extraPositionsOpen[reelIndex];
        (reel as UCReel).bottomBlocker.isOpenDataFromServer =
            UgliestCatch.model.data.extraPositionsOpen[reelIndex];

        const stopAnimationTimeline = gsap.timeline({});

        let hasScatter: boolean = false;
        finalDataReels[reelIndex].forEach((symbolName: string) => {
            if (symbolName === scatterSymbolName) {
                hasScatter = true;
            }
        });

        const data: IReelData = {
            hasNearWin: false,
            hasScatter: hasScatter,
            stopSymbolNames: finalDataReels[reelIndex],
            firstScatterReelToStop: false,
        };
        const reelLabel: string = `reelBeginStop${reelIndex}`;

        currentTimelinePosition += stopSpinDelay;

        data.firstScatterReelToStop = hasScatter && firstNearWinToStop;
        if (hasScatter) {
            firstNearWinToStop = false;
        }

        if (nearWinOnReels[reelIndex]) {
            timeline.addLabel(`${TimelineLabels.SHOW_NEAR_WIN_ANIMATION}${reelIndex}`, `>`);

            const reelNearWinAnimation = reel.getShowNearWinAnimation();

            timeline.add(
                reelNearWinAnimation,
                `${TimelineLabels.SHOW_NEAR_WIN_ANIMATION}${reelIndex}`
            );

            timeline.addLabel(
                reelLabel,
                `${TimelineLabels.SHOW_NEAR_WIN_ANIMATION}${reelIndex}+=${nearWinDuration}`
            );

            const stopReelAnimation = (reel as UCReel).stopNormalBetAnimation(
                data,
                onReelSpinComplete,
                onReelLand
            );

            stopAnimationTimeline.add(stopReelAnimation, 0);

            timeline.add(stopAnimationTimeline, reelLabel);

            timeline.addLabel(`reelStopped${reelIndex}`);
            timeline.addLabel(
                `${TimelineLabels.HIDE_NEAR_WIN_ANIMATION}${reelIndex}`,
                `reelStopped${reelIndex}`
            );

            timeline.add(
                reel.getHideNearWinAnimation(),
                `${TimelineLabels.HIDE_NEAR_WIN_ANIMATION}${reelIndex}`
            );
        } else {
            timeline.addLabel(reelLabel, currentTimelinePosition);

            stopAnimationTimeline.add(
                (reel as UCReel).stopNormalBetAnimation(data, onReelSpinComplete, onReelLand),
                0
            );

            timeline.add(stopAnimationTimeline, reelLabel);

            timeline.addLabel(`reelStopped${reelIndex}`);
        }
    });

    let shoudPlayOpenBlockerSound: boolean = false;

    gameReels.forEach((reel, reelIndex) => {
        const topBlocker = (reel as UCReel).topBlocker;
        const bottomBlocker = (reel as UCReel).bottomBlocker;

        let hasScatter: boolean = false;
        finalDataReels[reelIndex].forEach((symbolName: string) => {
            if (symbolName === scatterSymbolName) {
                hasScatter = true;
            }
        });

        if (hasScatter) {
            const openBlockerLabel: string = `openBlocker${reelIndex}`;
            timeline.addLabel(openBlockerLabel, `reelStopped${reelIndex}`);
            timeline.add(topBlocker.open(), openBlockerLabel);
            timeline.add(bottomBlocker.open(), openBlockerLabel);

            if (!shoudPlayOpenBlockerSound) {
                shoudPlayOpenBlockerSound = true;
                timeline.call(
                    () => {
                        playBlockerOpenSound();
                    },
                    undefined,
                    openBlockerLabel
                );
            }
        }
    });

    timeline.addLabel(TimelineLabels.SCATTER_DECORATOR_COMPLETE, `>`);

    /* const entries = Object.entries(timeline.labels);

    const salam = entries.sort((a, b) => {
        return timeline.labels[a[0]] - timeline.labels[b[0]];
    });

    console.log(salam);*/

    // timeline.timeScale(0.2);
};
